import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Topbar } from '@/components/layout/Topbar';
import SuiteQuestionnaireBlock from '@/components/admin/SuiteQuestionnaireBlock';
import TalentTrainShell from '@/pages/talenttrain/TalentTrainShell';
import AdminTalentTrainWorkspace from '@/pages/admin/AdminTalentTrainWorkspace';
import { adminApi } from '@/lib/adminApi';
import { useApp } from '@/context/AppContext';
import SuiteDocSection from '@/components/admin/SuiteDocSection';
import SuiteOpsPanel from '@/components/admin/SuiteOpsPanel';

/**
 * Admin TalentTrain console (Prompt 20). The admin must pick a company first;
 * deep-linked /admin/talenttrain/:userId always carries the company already
 * selected in localStorage (or auto-resolves it from the user record).
 */
export default function AdminTalentTrain() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { user: authUser } = useApp();
  // Founder authority model (2026-08): HM Advisor / HM Admin are READ-ONLY on
  // the client's official training plan; they advise via Observations & Follow-up.
  const canEdit = false;
  const canDiscuss = ['hm_advisor', 'hm_admin'].includes(authUser?.role);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    adminApi.listCompanies().then((cos) => {
      setCompanies(cos);
      const stored = localStorage.getItem('cth_tt_company');
      const firstId = stored && cos.some((c) => c.id === stored) ? stored : cos[0]?.id;
      if (firstId) setCompanyId(firstId);
    });
  }, []);

  // If a deep-link to a user is present, resolve their company.
  useEffect(() => {
    if (!userId || companyId) return;
    adminApi.listAllUsers().then((users) => {
      const u = users.find((x) => x.id === userId);
      if (u?.company_id) setCompanyId(u.company_id);
    }).catch(() => {});
  }, [userId, companyId]);

  useEffect(() => {
    if (companyId) localStorage.setItem('cth_tt_company', companyId);
  }, [companyId]);

  const api = useMemo(() => ({
    list:    () => adminApi.ttListUsers(companyId),
    getUser: (uid) => adminApi.ttGetUser(companyId, uid),
    create:  (uid, body) => adminApi.ttCreateAction(companyId, uid, body),
    update:  (aid, patch) => adminApi.ttUpdateAction(companyId, aid, patch),
    remove:  (aid) => adminApi.ttDeleteAction(companyId, aid),
    discussionGet: (uid) => adminApi.ttDiscussionGet(companyId, uid),
    discussionPost: (uid, text) => adminApi.ttDiscussionPost(companyId, uid, text),
    discussionRead: (uid) => adminApi.ttDiscussionRead(companyId, uid),
    discussionNextAction: (uid, text) => adminApi.ttDiscussionNextAction(companyId, uid, text),
    discussionUnread: () => adminApi.ttDiscussionUnread(companyId),
  }), [companyId]);

  if (!companyId) {
    return (
      <>
        <Topbar title="TalentTrain" subtitle={t('pages.adminTalentTrain.pickCompany')} />
        <div className="p-6 lg:p-8">
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="w-72 h-9" data-testid="tt-company-select"><SelectValue placeholder={t('pages.adminTalentTrain.companyPh')} /></SelectTrigger>
            <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </>
    );
  }

  return (
    <div data-testid="admin-talenttrain-root" data-company-id={companyId}>
      <div className="px-6 lg:px-8 pt-4 space-y-6">
        <div className="flex items-center justify-end">
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="w-60 h-8 text-xs" data-testid="tt-company-select"><SelectValue /></SelectTrigger>
            <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {/* Founder UAT — development-plan supervision layer (role-aware) */}
        {!userId && (
          <SuiteOpsPanel suiteCode="talenttrain" onClientOpen={(cid) => setCompanyId(cid)} />
        )}
      </div>
      {/* Per-user deep-link keeps the existing collaborator shell. */}
      <TalentTrainShell
        portal="admin"
        canEdit={canEdit}
        canDiscuss={canDiscuss}
        api={api}
        initialUserId={userId || null}
      />
      {/* Minimal HM workspace on the landing page (read-only). */}
      {!userId && (
        <div className="px-6 lg:px-8 pb-6 lg:pb-8">
          <AdminTalentTrainWorkspace companyId={companyId} />
        </div>
      )}
      <div className="px-6 lg:px-8 pb-6 lg:pb-8 space-y-6">
        <SuiteQuestionnaireBlock suiteCode="talenttrain" suiteName="TalentTrain" />
        <SuiteDocSection suiteCode="talenttrain" />
      </div>
    </div>
  );
}
